import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { header } from '../components/header.encry';
import ProductService from '../components/ProductService';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Box,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  InputAdornment,
  Stack,
  Pagination,
  Tabs,
  Tab,
  Badge,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

const InternalOrderCreate = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('Internal Order');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [currentCartItem, setCurrentCartItem] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const hasFetchedProducts = useRef(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(24);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Order history state
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [orderFilters, setOrderFilters] = useState({
    store_id: '',
    orderStatus: '',
    orderDate: '',
  });
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);

  // Resolve image URL with the same logic as ProductTable
  const resolveImageUrl = (path) => {
    if (!path) return null;

    const normalized = `${path}`.replace(/^\//, "");
    if (normalized.startsWith("http")) {
      return normalized;
    }

    return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
  };

  // Fetch order history
  const fetchOrderHistory = useCallback(async () => {
    setIsLoadingOrders(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (orderFilters.store_id) params.append('store_id', orderFilters.store_id);
      if (orderFilters.orderStatus) params.append('orderStatus', orderFilters.orderStatus);
      if (orderFilters.orderDate) params.append('orderDate', orderFilters.orderDate);
      
      const response = await axios.get(`${API_URL}/orders/type/Internal?${params.toString()}`, header());
      
      if (response.data && response.data.orders) {
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
      toast.error('Erreur lors du chargement de l\'historique des commandes');
    } finally {
      setIsLoadingOrders(false);
    }
  }, [orderFilters]);

  // Fetch orders when tab switches or filters change
  useEffect(() => {
    if (activeTab === 1) {
      fetchOrderHistory();
    }
  }, [activeTab, fetchOrderHistory]);

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await axios.get(`${API_URL}/stores`, header());
        const storesList = response.data?.data || response.data || [];
        setStores(storesList);
      } catch (error) {
        console.error('Error fetching stores:', error);
        toast.error('Erreur lors du chargement des magasins');
      }
    };
    fetchStores();
  }, []);

  // Fetch products from local cache first, then refresh the shared stream in background.
  const fetchAllProducts = useCallback(async () => {
    if (hasFetchedProducts.current) {
      return;
    }
    hasFetchedProducts.current = true;
    setIsLoadingProducts(true);
    try {
      const allProducts = await ProductService.fetchProducts();
      setProducts(allProducts);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      toast.error('Erreur lors du chargement des produits');
      hasFetchedProducts.current = false;
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  useEffect(() => {
    const handleProductsCacheUpdated = (event) => {
      const freshProducts = Array.isArray(event?.detail?.products) ? event.detail.products : [];
      if (freshProducts.length) {
        setProducts(freshProducts);
      }
    };

    window.addEventListener(ProductService.PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    return () => {
      window.removeEventListener(ProductService.PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    };
  }, []);

  // Filter products based on search query
  const filteredProductsAll = useMemo(() => {
    let result = [];
    if (!searchQuery.trim()) {
      result = [...products];
    } else {
      const query = searchQuery.toLowerCase();
      result = products.filter(product => 
        (product.name && String(product.name).toLowerCase().includes(query)) ||
        (product.barcode && String(product.barcode).toLowerCase().includes(query)) ||
        (product.description && String(product.description).toLowerCase().includes(query))
      );
    }
    
    // Sort by created_at descending (newest first)
    return result.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [products, searchQuery]);

  // Paginated products
  const filteredProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProductsAll.slice(startIndex, endIndex);
  }, [filteredProductsAll, currentPage, itemsPerPage]);

  // Total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredProductsAll.length / itemsPerPage);
  }, [filteredProductsAll, itemsPerPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Add product to cart
  const addToCart = (product) => {
    const existingItem = cart.find(item => item.productId === product._id);
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.productId === product._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
      toast.success('Quantité mise à jour');
    } else {
      const newCartItem = {
        productId: product._id,
        title: product.name,
        urlImage: resolveImageUrl(product.url_product) || '',
        quantity: 1,
        price: product.regular_price || 0,
        product: product,
        options: []
      };
      setCart([...cart, newCartItem]);
      toast.success('Produit ajouté au panier');
    }
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.productId !== productId));
    toast.info('Produit retiré du panier');
  };

  // Update quantity
  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.productId === productId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  // Open options modal
  const openOptionsModal = (cartItem) => {
    setCurrentCartItem(cartItem);
    setSelectedOptions(cartItem.options || []);
    setOptionsModalOpen(true);
  };

  // Save options
  const saveOptions = () => {
    if (currentCartItem) {
      setCart(cart.map(item => 
        item.productId === currentCartItem.productId
          ? { ...item, options: selectedOptions }
          : item
      ));
      toast.success('Options enregistrées');
    }
    setOptionsModalOpen(false);
    setCurrentCartItem(null);
    setSelectedOptions([]);
  };

  // Add new option
  const addOption = () => {
    setSelectedOptions([...selectedOptions, { quantity: '1', title: '', remote_option_id: '' }]);
  };

  // Update option
  const updateOption = (index, field, value) => {
    const updated = [...selectedOptions];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedOptions(updated);
  };

  // Remove option
  const removeOption = (index) => {
    setSelectedOptions(selectedOptions.filter((_, i) => i !== index));
  };

  // Calculate total
  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (Number(item.price) || 0) * item.quantity, 0);
  };

  // Submit order
  const submitOrder = async () => {
    if (!selectedStore) {
      toast.error('Veuillez sélectionner un magasin');
      return;
    }

    if (cart.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }

    const payload = {
      store_id: selectedStore._id,
      products: cart.map(item => ({
        id_product: item.productId,
        title: item.title,
        urlImage: item.urlImage,
        quantity: String(item.quantity),
        price: String(item.price),
        options: item.options || []
      })),
      CustomerName: customerName || 'Internal Order',
      note: note || '',
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/orders/internal`, payload, header());
      const order = response.data?.order || response.data;
      setCreatedOrder(order);
      setSuccessDialogOpen(true);
      
      // Reset form
      setCart([]);
      setCustomerName('Internal Order');
      setNote('');
      
      // Refresh order history in background
      fetchOrderHistory();
      
      toast.success('Commande interne créée avec succès!');
    } catch (error) {
      console.error('Error creating order:', error);
      const message = error?.response?.data?.error || error?.response?.data?.message || error.message;
      toast.error(`Erreur: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setCreatedOrder(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1F2937', mb: 3 }}>
        <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Créer une commande interne
      </Typography>

      {/* Tabs for Create / History */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Créer une commande" />
          <Tab 
            label={
              <Badge badgeContent={orders.length} color="primary">
                Historique
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* Tab Panel 0 - Create Order */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Left Column - Product Selection */}
          <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sélectionner un magasin
            </Typography>
            <Autocomplete
              options={stores}
              getOptionLabel={(option) => option.name || ''}
              value={selectedStore}
              onChange={(e, newValue) => setSelectedStore(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Magasin" placeholder="Choisir un magasin" />
              )}
              fullWidth
            />
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Rechercher des produits
            </Typography>
            
            <TextField
              fullWidth
              placeholder="Rechercher par nom, code-barres..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredProductsAll.length} produit{filteredProductsAll.length !== 1 ? 's' : ''} trouvé{filteredProductsAll.length !== 1 ? 's' : ''}
              </Typography>
              {totalPages > 1 && (
                <Pagination 
                  count={totalPages} 
                  page={currentPage} 
                  onChange={(e, page) => setCurrentPage(page)}
                  size="small"
                  color="primary"
                />
              )}
            </Box>

            {isLoadingProducts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <Grid container spacing={2}>
                    {filteredProducts.map((product) => (
                      <Grid item xs={12} sm={6} md={4} key={product._id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          {product.url_product && (
                            <CardMedia
                              component="img"
                              height="140"
                              image={resolveImageUrl(product.url_product)}
                              alt={product.name}
                              sx={{ objectFit: 'cover' }}
                            />
                          )}
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" gutterBottom noWrap>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {Number(product.regular_price || 0).toFixed(2)} DHS
                            </Typography>
                            {product.barcode && (
                              <Chip label={product.barcode} size="small" sx={{ mt: 1 }} />
                            )}
                          </CardContent>
                          <Box sx={{ p: 1 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              onClick={() => addToCart(product)}
                              startIcon={<AddIcon />}
                            >
                              Ajouter
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination 
                      count={totalPages} 
                      page={currentPage} 
                      onChange={(e, page) => setCurrentPage(page)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Cart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Panier ({cart.length})
            </Typography>
            
            <Divider sx={{ my: 2 }} />

            <TextField
              fullWidth
              label="Nom du client"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 3, maxHeight: '400px', overflowY: 'auto' }}>
              {cart.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Panier vide
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {cart.map((item) => (
                    <Card key={item.productId} variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                            {item.title}
                          </Typography>
                          <IconButton size="small" onClick={() => removeFromCart(item.productId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => updateQuantity(item.productId, -1)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2">{item.quantity}</Typography>
                          <IconButton size="small" onClick={() => updateQuantity(item.productId, 1)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                            {(Number(item.price) * item.quantity).toFixed(2)} DHS
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => openOptionsModal(item)}
                          sx={{ mt: 1 }}
                        >
                          {item.options?.length > 0 ? `Options (${item.options.length})` : 'Ajouter options'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                Total: {calculateTotal().toFixed(2)} DHS
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={submitOrder}
              disabled={isSubmitting || cart.length === 0 || !selectedStore}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {isSubmitting ? 'Création...' : 'Créer la commande'}
            </Button>
          </Paper>
        </Grid>
      </Grid>
      )}

      {/* Tab Panel 1 - Order History */}
      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={stores}
                  getOptionLabel={(option) => option.name || ''}
                  value={stores.find(s => (s._id || s.store_id) === orderFilters.store_id) || null}
                  onChange={(e, newValue) => setOrderFilters({ ...orderFilters, store_id: newValue?._id || newValue?.store_id || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Magasin" size="small" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={orderFilters.orderStatus}
                    label="Statut"
                    onChange={(e) => setOrderFilters({ ...orderFilters, orderStatus: e.target.value })}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="accepted">Accepté</MenuItem>
                    <MenuItem value="ready">Prêt</MenuItem>
                    <MenuItem value="completed">Complété</MenuItem>
                    <MenuItem value="gone">Parti</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={orderFilters.orderDate}
                  onChange={(e) => setOrderFilters({ ...orderFilters, orderDate: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchOrderHistory}
                  disabled={isLoadingOrders}
                >
                  Actualiser
                </Button>
              </Grid>
            </Grid>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              {orders.length} commande(s) trouvée(s)
            </Typography>
          </Paper>

          {/* Orders Table */}
          <Paper sx={{ p: 3 }}>
            {isLoadingOrders ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : orders.length === 0 ? (
              <Typography variant="body1" sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                Aucune commande trouvée
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>N° Commande</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Heure</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Magasin</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Produits</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.orderNumber} hover>
                        <TableCell>{order.orderNumber}</TableCell>
                        <TableCell>{order.orderCode}</TableCell>
                        <TableCell>{order.orderDate}</TableCell>
                        <TableCell>{order.orderTime}</TableCell>
                        <TableCell>{order.CustomerName}</TableCell>
                        <TableCell>
                          {stores.find(s => (s._id || s.store_id) === order.store_id)?.name || order.store_id}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.orderStatus} 
                            size="small"
                            color={
                              order.orderStatus === 'completed' ? 'success' : 
                              order.orderStatus === 'ready' ? 'info' : 
                              order.orderStatus === 'accepted' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">{order.totalPrice} DHS</TableCell>
                        <TableCell align="center">
                          <Badge badgeContent={order.productsCount} color="primary" />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setSelectedOrderDetail(order);
                              setOrderDetailOpen(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Options Modal */}
      <Dialog open={optionsModalOpen} onClose={() => setOptionsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Options pour {currentCartItem?.title}
          <IconButton
            onClick={() => setOptionsModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Button
            startIcon={<AddIcon />}
            onClick={addOption}
            sx={{ mb: 2 }}
          >
            Ajouter une option
          </Button>

          <Stack spacing={2}>
            {selectedOptions.map((option, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="Quantité"
                      value={option.quantity}
                      onChange={(e) => updateOption(index, 'quantity', e.target.value)}
                      type="number"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={7}>
                    <TextField
                      fullWidth
                      label="Titre"
                      value={option.title}
                      onChange={(e) => updateOption(index, 'title', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton onClick={() => removeOption(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="ID Option (optionnel)"
                      value={option.remote_option_id}
                      onChange={(e) => updateOption(index, 'remote_option_id', e.target.value)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsModalOpen(false)}>Annuler</Button>
          <Button onClick={saveOptions} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Order Detail Modal */}
      <Dialog open={orderDetailOpen} onClose={() => setOrderDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails de la commande
          <IconButton
            onClick={() => setOrderDetailOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedOrderDetail && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">N° Commande</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedOrderDetail.orderNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Code</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedOrderDetail.orderCode}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Client</Typography>
                  <Typography variant="body1">{selectedOrderDetail.CustomerName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Magasin</Typography>
                  <Typography variant="body1">
                    {stores.find(s => (s._id || s.store_id) === selectedOrderDetail.store_id)?.name || selectedOrderDetail.store_id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Date</Typography>
                  <Typography variant="body1">{selectedOrderDetail.orderDate} {selectedOrderDetail.orderTime}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Statut</Typography>
                  <Chip 
                    label={selectedOrderDetail.orderStatus} 
                    size="small"
                    color={
                      selectedOrderDetail.orderStatus === 'completed' ? 'success' : 
                      selectedOrderDetail.orderStatus === 'ready' ? 'info' : 
                      selectedOrderDetail.orderStatus === 'accepted' ? 'warning' : 'default'
                    }
                  />
                </Grid>
              </Grid>

              {selectedOrderDetail.note && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Note</Typography>
                  <Typography variant="body1">{selectedOrderDetail.note}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Produits</Typography>
              
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Produit</TableCell>
                    <TableCell align="center">Quantité</TableCell>
                    <TableCell align="right">Prix</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrderDetail.products && selectedOrderDetail.products.map((product, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {product.url_product && (
                          <img 
                            src={resolveImageUrl(product.url_product)} 
                            alt={product.title}
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.title}</Typography>
                        {product.options && product.options.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {product.options.map(opt => `${opt.name}: ${opt.value}`).join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{product.quantity}</TableCell>
                      <TableCell align="right">{Number(product.price || 0).toFixed(2)} DHS</TableCell>
                      <TableCell align="right">{(Number(product.price || 0) * Number(product.quantity)).toFixed(2)} DHS</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" fontWeight="bold">{selectedOrderDetail.totalPrice} DHS</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDetailOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={closeSuccessDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Commande créée avec succès
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {createdOrder && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Commande #{createdOrder.orderNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Code: {createdOrder.orderCode}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2">
                <strong>Client:</strong> {createdOrder.CustomerName}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {createdOrder.orderDate} {createdOrder.orderTime}
              </Typography>
              <Typography variant="body2">
                <strong>Produits:</strong> {createdOrder.productsCount}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> {createdOrder.totalPrice} DHS
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSuccessDialog}>Fermer</Button>
          <Button 
            onClick={() => {
              setActiveTab(1);
              closeSuccessDialog();
            }} 
            variant="contained" 
            startIcon={<HistoryIcon />}
          >
            Voir dans l'historique
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InternalOrderCreate;
